import { useEffect, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { supabase } from "@project/lib/data-client";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Badge } from "@project/components/ui/badge";
import { toast } from "sonner";
import {
  Download,
  FileText,
  CheckCircle2,
  Search,
  Loader2,
  Sparkles,
} from "lucide-react";
import { downloadReceiptPDF, type ReceiptData } from "@project/lib/receipt-pdf";
import { generateYearlyStatementPDF } from "@project/lib/yearly-statement-pdf";
import { exportApiRowsToCsv } from "@project/lib/csv";
import { CsvExportButton } from "@project/admin/components/csv-export-button";
import { AiTextDialog } from "@project/admin/components/ai-text-dialog";
import { generateThankYou } from "@project/lib/ai.functions";

type Row = {
  id: string;
  receipt_no: string;
  amount: number;
  donation_date: string;
  payment_mode: string | null;
  payment_status: string | null;
  transaction_id: string | null;
  category: string | null;
  purpose: string | null;
  requires_80g: boolean | null;
  donor: ReceiptData["donor"];
  receipt: {
    id: string;
    receipt_no: string;
    status: string;
    generated_at: string;
  } | null;
};

export default function Page() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<
    "all" | "pending" | "no_receipt" | "generated"
  >("no_receipt");
  const [ttLang, setTtLang] = useState<"english" | "hindi" | "gujarati">(
    "english"
  );
  const [ttTone, setTtTone] = useState<"warm" | "formal" | "spiritual">("warm");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("donations")
      .select(
        `
        id, receipt_no, amount, donation_date, payment_mode, payment_status,
        transaction_id, category, purpose, requires_80g,
        donor:donors ( full_name, mobile, email, address, city, state, pincode, pan ),
        receipt:receipts ( id, receipt_no, status, generated_at )
      `
      )
      .order("donation_date", { ascending: false })
      .limit(500);
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const mapped: Row[] = (data ?? []).map((d: any) => ({
      ...d,
      donor: Array.isArray(d.donor) ? d.donor[0] : d.donor,
      receipt: Array.isArray(d.receipt) ? d.receipt[0] : d.receipt,
    }));
    setRows(mapped);
  };

  useEffect(() => {
    load();
  }, []);

  const nextReceiptNo = () => {
    const year = new Date().getFullYear();
    const existing = rows
      .map(r => r.receipt?.receipt_no)
      .filter(Boolean)
      .filter((s): s is string => !!s && s.startsWith(`JD-${year}-`))
      .map(s => parseInt(s.split("-")[2] || "0", 10))
      .filter(n => !isNaN(n));
    const next = (existing.length ? Math.max(...existing) : 0) + 1;
    return `JD-${year}-${String(next).padStart(4, "0")}`;
  };

  const confirmPayment = async (r: Row) => {
    setBusy(r.id);
    const { error } = await supabase
      .from("donations")
      .update({ payment_status: "success" })
      .eq("id", r.id);
    setBusy(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Payment marked as successful");
    load();
  };

  const generateReceipt = async (r: Row) => {
    if (r.payment_status !== "success") {
      toast.error("Confirm payment as successful before generating receipt");
      return;
    }
    setBusy(r.id);
    let receipt_no = r.receipt?.receipt_no;
    if (!r.receipt) {
      receipt_no = nextReceiptNo();
      const { error } = await supabase.from("receipts").insert({
        donation_id: r.id,
        receipt_no,
        status: "generated",
      });
      if (error) {
        setBusy(null);
        toast.error(error.message);
        return;
      }
    }
    downloadReceiptPDF({
      receipt_no: receipt_no!,
      donation_date: r.donation_date,
      amount: r.amount,
      payment_mode: r.payment_mode,
      transaction_id: r.transaction_id,
      purpose: r.purpose,
      category: r.category,
      requires_80g: r.requires_80g,
      donor: r.donor,
    });
    setBusy(null);
    toast.success(`Receipt ${receipt_no} ready`);
    if (!r.receipt) {
      load();
    }
  };

  const filtered = rows.filter(r => {
    if (filter === "pending" && r.payment_status !== "pending") {
      return false;
    }
    if (
      filter === "no_receipt" &&
      (r.receipt || r.payment_status !== "success")
    ) {
      return false;
    }
    if (filter === "generated" && !r.receipt) {
      return false;
    }
    if (q) {
      const hay =
        `${r.receipt_no} ${r.donor?.full_name ?? ""} ${r.donor?.mobile ?? ""} ${r.receipt?.receipt_no ?? ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export");
      return;
    }
    exportApiRowsToCsv(
      `receipts-${new Date().toISOString().slice(0, 10)}`,
      filtered as unknown as Record<string, unknown>[]
    );
    toast.success(`Exported ${filtered.length} rows`);
  };

  const fyOf = (dateStr: string) => {
    const d = new Date(dateStr);
    const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  };

  const exportYearlyStatement = (
    donorId: string | undefined,
    donorName: string,
    fy: string
  ) => {
    if (!donorId) {
      return toast.error("Anonymous donations have no consolidated statement");
    }
    const donorRows = rows.filter(
      r =>
        (r as any).donor_id === donorId ||
        (r.donor as any)?.id === donorId ||
        (r.donor && r.donor.full_name === donorName)
    );
    // Fallback: filter by donor full_name & FY from currently loaded rows
    const matched = rows.filter(
      r =>
        r.donor?.full_name === donorName &&
        fyOf(r.donation_date) === fy &&
        r.payment_status === "success"
    );
    const list = (donorRows.length ? donorRows : matched).filter(
      r => fyOf(r.donation_date) === fy && r.payment_status === "success"
    );
    if (!list.length) {
      return toast.error(`No successful donations in FY ${fy}`);
    }
    const total = list.reduce((s, r) => s + Number(r.amount ?? 0), 0);
    const doc = generateYearlyStatementPDF({
      fy,
      donor: list[0].donor as any,
      donations: list.map(r => ({
        receipt_no: r.receipt?.receipt_no ?? r.receipt_no,
        donation_date: r.donation_date,
        amount: r.amount,
        payment_mode: r.payment_mode,
        purpose: r.purpose,
      })),
    });
    doc.save(`80G-Statement-${donorName.replace(/\s+/g, "_")}-FY${fy}.pdf`);
    toast.success(`80G statement (₹${total.toLocaleString("en-IN")}) ready`);
  };

  // group donors for export menu (unique donor + FYs they have)
  const donorFYs = Array.from(
    new Set(
      rows
        .filter(r => r.donor?.full_name && r.payment_status === "success")
        .map(r => `${r.donor!.full_name}||${fyOf(r.donation_date)}`)
    )
  )
    .map(k => {
      const [name, fy] = k.split("||");
      return { name, fy };
    })
    .sort((a, b) => b.fy.localeCompare(a.fy) || a.name.localeCompare(b.name));

  return (
    <AdminShell
      title="Receipts (80G)"
      subtitle="Confirm payments, generate receipts and consolidated 80G yearly statements"
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search donor, mobile, receipt no..."
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <div className="bg-muted/40 flex gap-1 rounded-lg border p-1">
          {(
            [
              ["no_receipt", "Awaiting Receipt"],
              ["pending", "Pending Payment"],
              ["generated", "Generated"],
              ["all", "All"],
            ] as const
          ).map(([v, l]) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              className={`rounded-md px-3 py-1.5 text-xs transition ${filter === v ? "bg-background font-medium shadow-sm" : "text-muted-foreground"}`}
            >
              {l}
            </button>
          ))}
        </div>
        <CsvExportButton onExport={exportCsv} />
      </div>

      {donorFYs.length > 0 && (
        <details className="mb-4 rounded-xl border bg-card p-4">
          <summary className="cursor-pointer text-sm font-semibold">
            80G Yearly Statements ({donorFYs.length} donor·FY combinations)
          </summary>
          <div className="mt-3 grid max-h-64 gap-2 overflow-auto sm:grid-cols-2 lg:grid-cols-3">
            {donorFYs.slice(0, 60).map(({ name, fy }) => (
              <button
                key={name + fy}
                onClick={() => exportYearlyStatement(undefined, name, fy)}
                className="flex items-center justify-between gap-2 rounded-lg border bg-background px-3 py-2 text-left text-xs hover:bg-muted"
              >
                <span className="truncate">
                  <span className="font-medium">{name}</span> · FY {fy}
                </span>
                <Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        </details>
      )}

      <div className="overflow-hidden rounded-xl border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="p-3 text-left">Donor</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-right">Amount</th>
                <th className="p-3 text-left">Payment</th>
                <th className="p-3 text-left">Receipt</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-8 text-center text-muted-foreground"
                  >
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-8 text-center text-muted-foreground"
                  >
                    No records match.
                  </td>
                </tr>
              )}
              {filtered.map(r => (
                <tr key={r.id} className="hover:bg-muted/20 border-t">
                  <td className="p-3">
                    <div className="font-medium">
                      {r.donor?.full_name ?? "Anonymous"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.donor?.mobile ?? "—"} · {r.donor?.city ?? "—"}
                    </div>
                    {r.donor?.pan && (
                      <div className="text-xs text-muted-foreground">
                        PAN: {r.donor.pan}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap p-3">
                    {new Date(r.donation_date).toLocaleDateString("en-IN")}
                    <div className="text-xs text-muted-foreground">
                      {r.receipt_no}
                    </div>
                  </td>
                  <td className="whitespace-nowrap p-3 text-right font-semibold">
                    ₹{r.amount.toLocaleString("en-IN")}
                  </td>
                  <td className="p-3">
                    <Badge
                      variant={
                        r.payment_status === "success"
                          ? "default"
                          : r.payment_status === "pending"
                            ? "secondary"
                            : "destructive"
                      }
                    >
                      {r.payment_status ?? "—"}
                    </Badge>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {r.payment_mode ?? "—"}
                    </div>
                  </td>
                  <td className="p-3">
                    {r.receipt ? (
                      <div>
                        <Badge
                          variant="outline"
                          className="border-green-500 text-green-700"
                        >
                          {r.receipt.receipt_no}
                        </Badge>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {new Date(r.receipt.generated_at).toLocaleDateString(
                            "en-IN"
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        Not generated
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      {r.payment_status === "pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => confirmPayment(r)}
                          disabled={busy === r.id}
                        >
                          <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Confirm
                        </Button>
                      )}
                      {r.payment_status === "success" && !r.receipt && (
                        <Button
                          size="sm"
                          onClick={() => generateReceipt(r)}
                          disabled={busy === r.id}
                        >
                          {busy === r.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <FileText className="mr-1 h-3.5 w-3.5" />
                          )}
                          Generate
                        </Button>
                      )}
                      {r.receipt && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateReceipt(r)}
                          disabled={busy === r.id}
                        >
                          <Download className="mr-1 h-3.5 w-3.5" /> Download
                        </Button>
                      )}
                      {r.payment_status === "success" && (
                        <AiTextDialog
                          title={`AI Thank-you · ${r.donor?.full_name ?? "Donor"}`}
                          description="Personalised note referencing this donation and lifetime giving."
                          controls={
                            <div className="flex flex-wrap gap-2 text-xs">
                              <select
                                value={ttLang}
                                onChange={e => setTtLang(e.target.value as any)}
                                className="rounded border px-2 py-1"
                              >
                                <option value="english">English</option>
                                <option value="hindi">हिन्दी</option>
                                <option value="gujarati">ગુજરાતી</option>
                              </select>
                              <select
                                value={ttTone}
                                onChange={e => setTtTone(e.target.value as any)}
                                className="rounded border px-2 py-1"
                              >
                                <option value="warm">Warm</option>
                                <option value="formal">Formal</option>
                                <option value="spiritual">Spiritual</option>
                              </select>
                            </div>
                          }
                          generate={async () =>
                            (
                              await generateThankYou({
                                data: {
                                  donationId: r.id,
                                  language: ttLang,
                                  tone: ttTone,
                                },
                              })
                            ).text
                          }
                          trigger={
                            <Button size="sm" variant="outline">
                              <Sparkles className="mr-1 h-3.5 w-3.5" />{" "}
                              Thank-you
                            </Button>
                          }
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminShell>
  );
}
